import { ReactNode } from 'react';
import { Controller, ControllerProps } from 'react-hook-form';
import { TypographyVariant } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

type Props = ControllerProps & {
  viewingMode: ViewingMode;
  displayValue?: string;
  display?: ReactNode;
  label?: string;
  alwaysShowHeader?: boolean;
  hideOnViewingWhenEmpty?: boolean;
  labelVariant?: TypographyVariant;
  valueVariant?: TypographyVariant;
};

const ViewingModeController = (props: Readonly<Props>): React.JSX.Element => {
  const editingOrCreatingControllerRender: ControllerProps['render'] = ({ field, fieldState, formState }) => (
    <>
      {props.alwaysShowHeader && props.label && (
        <Typography variant={props.labelVariant ?? 'h5'} data-testid="viewingModeControllerLabel">
          {props.label}
        </Typography>
      )}
      {props.render({ field, fieldState, formState })}
    </>
  );

  const viewingControllerRender: ControllerProps['render'] = ({ field }) => {
    if (props.hideOnViewingWhenEmpty && (!field.value || field.value === '')) {
      return <></>;
    } else {
      return (
        <>
          {props.label && (
            <Typography variant={props.labelVariant ?? 'h5'} data-testid="viewingModeControllerLabel">
              {props.label}
            </Typography>
          )}
          {props.display ?? (
            <Typography
              variant={props.valueVariant ?? 'subtitle1'}
              data-testid="viewingModeControllerDisplayValue"
              sx={{ whiteSpace: 'pre-line', wordBreak: 'break-all' }}
            >
              {props.displayValue ?? field.value}
            </Typography>
          )}
        </>
      );
    }
  };

  const controllerPropsFactory: Readonly<Props> = {
    ...props,
    render: (renderConfig) => {
      switch (props.viewingMode) {
        case 'creation':
        case 'editing':
          return editingOrCreatingControllerRender(renderConfig);
        case 'viewing':
          return viewingControllerRender(renderConfig);
        default:
          return <></>;
      }
    },
  };

  return <Controller {...controllerPropsFactory}></Controller>;
};

export default ViewingModeController;
